import { FormicariumLayer } from '../layers/formicarium';
import { EventBus, GameEventTypes } from '@antification/core';
import { Dimensions } from './GameCanvas';
import { TransferableEntity } from '@antification/protocol';

/**
 * Manages multiple ant colonies.
 * All colonies update in the background, but only one is rendered at a time.
 */
export class ColonyManager {
  private colonies: FormicariumLayer[] = [];
  private selectedColonyIndex = 0;
  private events: EventBus;

  /**
   * Seed every colony's randomness derives from.
   *
   * Fixed by default, which means a fresh session always starts from the same
   * colony state. That is the point — it is what makes a run reproducible and
   * a server-side settlement checkable against a client's. Play still diverges
   * between sessions, because the player's own construction drives the colony.
   *
   * Pass a varying value (e.g. a save-file id) for per-session variety.
   */
  private worldSeed: string;

  /** Number of colonies to create */
  static readonly COLONY_COUNT = 3;

  /** Colony colors for visual differentiation */
  static readonly COLONY_COLORS = [
    { primary: '#8B4513', secondary: '#A0522D', name: 'Brown Colony' },   // Brown
    { primary: '#2F4F4F', secondary: '#556B2F', name: 'Dark Colony' },    // Dark/Forest
    { primary: '#8B0000', secondary: '#B22222', name: 'Red Colony' },     // Red/Fire ants
  ];

  constructor(events: EventBus, worldSeed = 'parkington') {
    this.events = events;
    this.worldSeed = worldSeed;

    // Listen for colony selection requests from any layer
    // ColonyManager is the single source of truth for selection
    this.events.on(GameEventTypes.COLONY_SELECT_REQUEST, (payload) => {
      const data = payload as { colonyIndex: number };
      this.selectColony(data.colonyIndex);
    });
  }

  /**
   * Create all colonies.
   */
  createColonies(): FormicariumLayer[] {
    this.colonies = [];

    for (let i = 0; i < ColonyManager.COLONY_COUNT; i++) {
      const colony = new FormicariumLayer(this.events, i, this.worldSeed);
      this.colonies.push(colony);
    }

    return this.colonies;
  }

  /**
   * Initialize all colonies.
   */
  async initAll(): Promise<void> {
    await Promise.all(this.colonies.map(colony => colony.init()));
  }

  /**
   * Update all colonies (they all run in the background).
   */
  update(dt: number): void {
    for (const colony of this.colonies) {
      colony.update(dt);
    }
  }

  /**
   * Render only the selected colony.
   */
  render(ctx: CanvasRenderingContext2D): void {
    const selectedColony = this.colonies[this.selectedColonyIndex];
    if (selectedColony) {
      selectedColony.render(ctx);
    }
  }

  /**
   * Resize all colonies.
   */
  resize(dimensions: Dimensions): void {
    for (const colony of this.colonies) {
      colony.resize(dimensions);
    }
  }

  /**
   * Get the currently selected colony.
   */
  getSelectedColony(): FormicariumLayer {
    return this.colonies[this.selectedColonyIndex];
  }

  /**
   * Get the selected colony index.
   */
  getSelectedIndex(): number {
    return this.selectedColonyIndex;
  }

  /**
   * Select a colony by index.
   */
  selectColony(index: number): void {
    if (index >= 0 && index < this.colonies.length) {
      this.selectedColonyIndex = index;
      this.events.emit(GameEventTypes.COLONY_SELECTED, {
        colonyIndex: index,
        colonyId: this.colonies[index].id,
      });
    }
  }

  /**
   * Cycle to the next colony.
   */
  selectNextColony(): void {
    this.selectColony((this.selectedColonyIndex + 1) % this.colonies.length);
  }

  /**
   * Cycle to the previous colony.
   */
  selectPreviousColony(): void {
    this.selectColony((this.selectedColonyIndex - 1 + this.colonies.length) % this.colonies.length);
  }

  /**
   * Get a colony by index.
   */
  getColony(index: number): FormicariumLayer | undefined {
    return this.colonies[index];
  }

  /**
   * Get a colony by its layer ID.
   */
  getColonyById(id: string): FormicariumLayer | undefined {
    return this.colonies.find(c => c.id === id);
  }

  /**
   * Get all colonies.
   */
  getAllColonies(): FormicariumLayer[] {
    return [...this.colonies];
  }

  /**
   * Get the number of colonies.
   */
  getColonyCount(): number {
    return this.colonies.length;
  }

  /**
   * Get colony info for display.
   */
  getColonyInfo(index: number): { name: string; color: string; population: number } | null {
    const colony = this.colonies[index];
    if (!colony) return null;

    const colors = ColonyManager.COLONY_COLORS[index] || ColonyManager.COLONY_COLORS[0];
    const stats = colony.getStats();

    return {
      name: colors.name,
      color: colors.primary,
      population: stats.currentPopulation,
    };
  }

  /**
   * Get all colony summaries for UI display.
   */
  getAllColonyInfo(): Array<{ name: string; color: string; population: number; selected: boolean }> {
    return this.colonies.map((colony, index) => {
      const colors = ColonyManager.COLONY_COLORS[index] || ColonyManager.COLONY_COLORS[0];
      const stats = colony.getStats();

      return {
        name: colors.name,
        color: colors.primary,
        population: stats.currentPopulation,
        selected: index === this.selectedColonyIndex,
      };
    });
  }

  /**
   * Route an entity transfer to the correct colony.
   *
   * Returns whether it arrived, per the transfer contract. A colony index nobody
   * dug is a refusal.
   */
  receiveEntity(entity: TransferableEntity, fromPortal: string, colonyIndex: number): boolean {
    const colony = this.colonies[colonyIndex];
    if (!colony) return false;
    return colony.receiveEntity(entity, fromPortal);
  }

  /**
   * Handle mouse move on the selected colony.
   */
  handleMouseMove(e: MouseEvent): void {
    const colony = this.getSelectedColony();
    if (colony?.handleMouseMove) {
      colony.handleMouseMove(e);
    }
  }

  /**
   * Handle click on the selected colony.
   */
  handleClick(e: MouseEvent): void {
    const colony = this.getSelectedColony();
    if (colony?.handleClick) {
      colony.handleClick(e);
    }
  }
}
